import { useCallback, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { formatDate } from "simplydo/core/dates";
import { OpenAPI } from "simplydo/interfaces";
import { styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";
import {
  Button,
  Dimmer,
  Divider,
  Header,
  Icon,
  Label,
  Popup,
} from "simplydo/react/ui";

import actions from "actions";
import api from "api";
import { useAppDispatch, useAppSelector } from "store";
import util from "utils/utils";

import { PlaceholderItem, PlaceholderStack } from "components/lib/UI";

const PopupContentContainer = styled.div`
  max-height: 100%;
  display: flex;
  gap: 16px;
  flex-direction: column;
  .challenges {
    overflow: auto;
  }
`;

const MobileNewIdeaButton = () => {
  const user = useAppSelector((state) => state.user);
  const dispatch = useAppDispatch();
  const onFollow = useCallback(
    (context: string, id: string) => {
      dispatch(actions.user.onFollow(context, id));
    },
    [dispatch],
  );

  const [challenges, setChallenges] = useState<OpenAPI.Schemas["Challenge"][]>(
    [],
  );
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const { t } = useTranslation();

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    api.challenges.getAll(
      {},
      ({ challenges }) => {
        const openChallenges = (challenges || []).filter(
          (c) => c.stage === "published",
        );
        setChallenges(openChallenges);
        setLoading(false);
      },
      () => {},
    );
  }, []);

  const newIdea = useCallback(
    (challenge: OpenAPI.Schemas["Challenge"]) => {
      window.scrollTo(0, 0);
      util.newIdea(challenge, user, (idea) => {
        navigate(`/ideas/${idea._id}`);
        if (user) {
          onFollow("challenge", idea.challenge);
        }
        setOpen(false);
      });
    },
    [user, navigate, onFollow],
  );

  const isIdeaPage = util.activePath(
    /\/ideas([A-z0-9])*\/?([A-z0-9])+\/?$/gi,
    location,
  );
  if (isIdeaPage) {
    return null;
  }

  const PopupContent = () => (
    <PopupContentContainer>
      <Header style={{ marginBottom: 0 }}>
        Create a new {t("generic.idea")} for...
      </Header>
      <div className="challenges">
        {loading ?
          <PlaceholderStack gap={5}>
            {Array.from({ length: 5 }).map((_, i) => (
              <PlaceholderItem key={i} height={50} />
            ))}
          </PlaceholderStack>
        : null}
        {challenges?.map((challenge) => (
          <div key={challenge._id} onClick={() => newIdea(challenge)}>
            <div style={{ display: "flex" }}>
              <div
                style={{
                  minWidth: "40px",
                  height: "40px",
                  overflow: "hidden",
                  backgroundImage: `url(${util.mixinCssUrlFallback(util.challengeImage(challenge), util.challengeImage())})`,
                  backgroundSize: "cover",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "center center",
                  borderRadius: 6,
                }}
              />
              <div style={{ marginLeft: 10 }}>
                <h4 style={{ marginBottom: 0 }}>{challenge.name}</h4>
                <Label
                  style={{ marginRight: 5, display: "inline-block" }}
                  circular
                  color={challenge?.deadline ? "blue" : "green"}
                  empty
                />
                <p style={{ display: "inline", fontSize: 13 }}>
                  {challenge?.deadline ?
                    `Closes ${formatDate(challenge?.deadline, "dd/MM/yy")}`
                  : "Open"}
                </p>
              </div>
            </div>
            <Divider />
          </div>
        ))}
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button onClick={() => navigate("/challenges/discover")}>
          View all {t("generic.challenges")}
        </Button>
      </div>
    </PopupContentContainer>
  );
  return (
    <div style={{ position: "fixed", bottom: 20, left: 20 }}>
      <Dimmer
        active={open}
        style={{ backgroundColor: "#0d0b0b8a", zIndex: 2147483001 }} // z-index hack to render on top of chatwoot
        onClickOutside={() => setOpen(false)}
        page
      />
      <Popup
        style={{ maxHeight: "55vh", minWidth: "90vw", zIndex: 2147483002 }}
        on="click"
        basic
        trigger={
          <Label
            circular
            style={{
              width: 60,
              height: 60,
              backgroundColor: theme.primaryColour,
              display: "flex",
            }}
          >
            <Icon
              name="plus"
              size="big"
              style={{ color: "white", margin: "auto" }}
            />
          </Label>
        }
        content={PopupContent}
        onClose={() => setOpen(false)}
        onOpen={() => setOpen(true)}
        open={open}
      />
    </div>
  );
};

export default MobileNewIdeaButton;
